import React, { useState, useEffect } from "react";
import { formatError } from "../utils/api";
import {
  ClipboardList, CheckCircle, Clock, AlertTriangle, MessageSquare,
  RefreshCw, ChevronDown, ChevronRight, Check, X, Circle,
} from "lucide-react";
import { fmtDateTime, fmtDate } from "../utils/dateUtils";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

const PRIORITY_STYLE = {
  urgent: { bg: "bg-red-100",    text: "text-red-700",     border: "border-red-200",   dot: "bg-red-500"    },
  high:   { bg: "bg-[#FBEAE7]", text: "text-[#C2624E]",   border: "border-[#C2624E]/30", dot: "bg-[#C2624E]" },
  medium: { bg: "bg-[#FDF4EB]", text: "text-[#D98032]",   border: "border-[#D98032]/30", dot: "bg-[#D98032]" },
  low:    { bg: "bg-[#F4F3F0]", text: "text-[#8A7E73]",   border: "border-[#E5E1DB]",  dot: "bg-[#C4BAAF]"  },
};

const STATUS_STYLE = {
  pending:   { label: "Pending",    icon: Clock,         color: "text-[#D98032]",  bg: "bg-[#FDF4EB]"  },
  in_review: { label: "In Review",  icon: MessageSquare, color: "text-blue-700",   bg: "bg-blue-50"    },
  resolved:  { label: "Resolved",   icon: CheckCircle,   color: "text-[#637C64]",  bg: "bg-[#EDF2EE]"  },
  dismissed: { label: "Dismissed",  icon: X,             color: "text-[#8A7E73]",  bg: "bg-[#F4F3F0]"  },
};

function ReviewCard({ item, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const pStyle = PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.medium;
  const sStyle = STATUS_STYLE[item.status] || STATUS_STYLE.pending;
  const StatusIcon = sStyle.icon;

  const handleStatusUpdate = async (newStatus) => {
    setSaving(true);
    await onUpdate(item.id, { status: newStatus, notes });
    setSaving(false);
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    await onUpdate(item.id, { notes });
    setShowNotes(false);
    setSaving(false);
  };

  let contextData = null;
  try { if (item.context) contextData = JSON.parse(item.context); } catch (e) { /* JSON.parse can throw on malformed context */ }

  return (
    <div className={`bg-white border-2 ${pStyle.border} rounded-2xl overflow-hidden`} data-testid={`review-item-${item.id}`}>
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Priority dot */}
            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${pStyle.dot}`} />
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${pStyle.bg} ${pStyle.text}`}>
                  {item.priority}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${sStyle.bg} ${sStyle.color}`}>
                  <StatusIcon size={9} /> {sStyle.label}
                </span>
                <span className="text-[10px] bg-[#F4F3F0] text-[#8A7E73] px-2 py-0.5 rounded-full">
                  {item.module}
                </span>
              </div>

              {/* Title */}
              <h3 className="font-bold text-[#3A322C] text-sm leading-snug">{item.title}</h3>
              <p className="text-xs text-[#8A7E73] mt-1">
                Added by {item.created_by} · {fmtDateTime(item.created_at)}
              </p>
            </div>
          </div>

          {/* Expand toggle */}
          <button onClick={() => setExpanded(!expanded)} className="text-[#8A7E73] hover:text-[#5C5147] shrink-0">
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        {/* Description preview */}
        {!expanded && (
          <p className="text-sm text-[#5C5147] mt-3 ml-5 line-clamp-2 leading-relaxed">
            {item.description}
          </p>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-[#F4F3F0] px-5 py-4 space-y-4">
          {/* Full description */}
          <div>
            <p className="text-xs font-bold uppercase text-[#8A7E73] mb-1.5">Description</p>
            <p className="text-sm text-[#5C5147] whitespace-pre-line leading-relaxed">{item.description}</p>
          </div>

          {/* Action required */}
          {item.action_required && (
            <div className="bg-[#FDF4EB] border border-[#D98032]/20 rounded-xl p-4">
              <p className="text-xs font-bold uppercase text-[#D98032] mb-1.5 flex items-center gap-1">
                <AlertTriangle size={11} /> Action Required
              </p>
              <p className="text-sm text-[#5C5147] whitespace-pre-line leading-relaxed">{item.action_required}</p>
            </div>
          )}

          {/* Context data */}
          {contextData && (
            <div>
              <p className="text-xs font-bold uppercase text-[#8A7E73] mb-1.5">Context / Data</p>
              <div className="bg-[#F4F3F0] rounded-xl p-3 grid grid-cols-2 gap-2">
                {Object.entries(contextData).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] text-[#8A7E73] uppercase font-bold">{k.replace(/_/g, " ")}</p>
                    <p className="text-xs font-medium text-[#3A322C] font-mono">{String(v)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-bold uppercase text-[#8A7E73]">Notes / Response</p>
              {!showNotes && (
                <button onClick={() => setShowNotes(true)} className="text-xs text-[#E8956D] font-bold hover:underline">
                  {item.notes ? "Edit" : "+ Add note"}
                </button>
              )}
            </div>
            {showNotes ? (
              <div className="space-y-2">
                <textarea
                  rows={3}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add your response or notes here..."
                  className="w-full border border-[#E5E1DB] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveNotes} disabled={saving}
                    className="flex items-center gap-1 bg-[#637C64] text-white rounded-lg px-3 py-1.5 text-xs font-bold">
                    <Check size={11} /> Save
                  </button>
                  <button onClick={() => { setShowNotes(false); setNotes(item.notes || ""); }}
                    className="text-xs text-[#8A7E73] border border-[#E5E1DB] rounded-lg px-3 py-1.5 font-bold">
                    Cancel
                  </button>
                </div>
              </div>
            ) : item.notes ? (
              <div className="bg-[#EDF2EE] rounded-xl p-3 text-sm text-[#5C5147]">{item.notes}</div>
            ) : (
              <p className="text-xs text-[#C4BAAF] italic">No notes yet</p>
            )}
          </div>

          {/* Resolved info */}
          {item.resolved_by && (
            <p className="text-xs text-[#637C64]">
              <CheckCircle size={10} className="inline mr-1" />
              {item.status === "resolved" ? "Resolved" : "Dismissed"} by {item.resolved_by}
              {item.resolved_at && ` · ${fmtDate(item.resolved_at)}`}
            </p>
          )}

          {/* Action buttons */}
          {item.status !== "resolved" && item.status !== "dismissed" && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F4F3F0]">
              {/* Apply button for security entry edit/delete */}
              {contextData?.request_type && contextData?.entry_id && (
                <button onClick={async () => {
                  setSaving(true);
                  try {
                    await axios.post(`${API}/api/review/${item.id}/apply`, {}, auth());
                    window.location.reload();
                  } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
                  setSaving(false);
                }}
                  disabled={saving}
                  className={`flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg font-bold text-white ${contextData.request_type === "delete" ? "bg-[#C2624E] hover:bg-[#A8503E]" : "bg-[#637C64] hover:bg-[#4e6350]"}`}>
                  <Check size={11} />
                  {contextData.request_type === "delete" ? "Approve & Delete Entry" : "Approve & Apply Edit"}
                </button>
              )}
              <button onClick={() => handleStatusUpdate("in_review")} disabled={saving || item.status === "in_review"}
                className="flex items-center gap-1.5 text-xs border border-blue-200 text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5 font-bold hover:bg-blue-100 disabled:opacity-50">
                <MessageSquare size={11} /> Mark In Review
              </button>
              <button onClick={() => handleStatusUpdate("resolved")} disabled={saving}
                className="flex items-center gap-1.5 text-xs bg-[#637C64] text-white rounded-lg px-3 py-1.5 font-bold hover:bg-[#4e6350]">
                <Check size={11} /> Mark Resolved
              </button>
              <button onClick={() => handleStatusUpdate("dismissed")} disabled={saving}
                className="flex items-center gap-1.5 text-xs border border-[#E5E1DB] text-[#8A7E73] rounded-lg px-3 py-1.5 font-bold hover:bg-[#F4F3F0]">
                <X size={11} /> Dismiss
              </button>
              <button onClick={() => onDelete(item.id)} disabled={saving}
                className="flex items-center gap-1.5 text-xs text-[#C2624E] rounded-lg px-2 py-1.5 font-bold hover:bg-[#FBEAE7] ml-auto">
                Remove
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReviewRequired() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending_all");
  const [moduleFilter, setModuleFilter] = useState("");
  const [error, setError] = useState("");

  const fetchItems = async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (statusFilter === "pending_all") params.status = "pending";
      else if (statusFilter === "in_review") params.status = "in_review";
      else if (statusFilter === "resolved") params.status = "resolved";
      else if (statusFilter === "all") params.status = "all";
      if (moduleFilter) params.module = moduleFilter;
      const r = await axios.get(`${API}/api/review`, { ...auth(), params });
      setItems(r.data || []);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, [statusFilter, moduleFilter]);

  const handleUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/api/review/${id}`, data, auth());
      await fetchItems();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/review/${id}`, auth());
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) { setError(formatError(e)); }
  };

  const modules = [...new Set(items.map(i => i.module))].sort();
  const pendingCount = items.filter(i => i.status === "pending").length;
  const inReviewCount = items.filter(i => i.status === "in_review").length;

  const STATUS_TABS = [
    { id: "pending_all", label: "Pending",   count: pendingCount },
    { id: "in_review",   label: "In Review", count: inReviewCount },
    { id: "resolved",    label: "Resolved",  count: null },
    { id: "all",         label: "All",       count: null },
  ];

  return (
    <div className="space-y-5 max-w-4xl" data-testid="review-required">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
            <span className="w-10 h-10 bg-[#FDF4EB] rounded-xl flex items-center justify-center">
              <ClipboardList size={20} className="text-[#D98032]" />
            </span>
            Review Required
          </h1>
          <p className="text-sm text-[#8A7E73] mt-0.5 ml-14">
            Pending approvals, data questions, and items that need team input before proceeding
          </p>
        </div>
        <button onClick={fetchItems} disabled={loading}
          className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}

      {/* Status tabs */}
      <div className="flex gap-1 flex-wrap">
        {STATUS_TABS.map(tab => (
          <button key={tab.id} onClick={() => setStatusFilter(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${statusFilter === tab.id ? "bg-[#E8956D] text-[#3A322C]" : "bg-white border border-[#E5E1DB] text-[#5C5147] hover:bg-[#F4F3F0]"}`}>
            {tab.label}
            {tab.count !== null && tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-extrabold ${statusFilter === tab.id ? "bg-[#3A322C]/20 text-[#3A322C]" : "bg-[#E8956D] text-white"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
        {modules.length > 1 && (
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}
            className="ml-auto border border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E8956D] text-[#5C5147]">
            <option value="">All Modules</option>
            {modules.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="text-center py-20 bg-white border border-[#E5E1DB] rounded-2xl">
          <div className="w-16 h-16 bg-[#EDF2EE] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} className="text-[#637C64]" />
          </div>
          <h3 className="font-bold text-[#3A322C] mb-1">
            {statusFilter === "pending_all" ? "Nothing pending review!" : "No items found"}
          </h3>
          <p className="text-sm text-[#8A7E73]">
            {statusFilter === "pending_all"
              ? "All items have been reviewed. Great work!"
              : "Try a different filter."}
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <RefreshCw size={24} className="animate-spin text-[#E8956D] mx-auto" />
        </div>
      )}

      {/* Items list */}
      <div className="space-y-3">
        {items.map(item => (
          <ReviewCard key={item.id} item={item} onUpdate={handleUpdate} onDelete={handleDelete} />
        ))}
      </div>
    </div>
  );
}
